import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import { NotificationProvider, useNotifications } from './context/NotificationContext';
import { ToastProvider } from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandingProvider, useBranding } from './context/BrandingContext';
import { deriveSocialLabel } from './utils/socialLabel';
import { ThemeProvider } from './context/ThemeContext';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import OrgSwitcher from './components/OrgSwitcher';
import UserMenu from './components/UserMenu';
import ImpersonationSwitcher from './components/ImpersonationSwitcher';
import { superAdminUrl } from './utils/superAdminUrl';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/AdminLayout';
import UserRoute from './components/UserRoute';
import UserLayout from './components/UserLayout';
import SuperAdminRoute from './components/SuperAdminRoute';
import SuperAdminLayout from './components/SuperAdminLayout';
import AuthCallback from './components/AuthCallback';
import ImpersonateCallback from './components/ImpersonateCallback';
import OrgSwitchCallback from './components/OrgSwitchCallback';
import ChatBubble from './components/messaging/ChatBubble';
import NotificationBell from './components/messaging/NotificationBell';
import BroadcastModal from './components/messaging/BroadcastModal';
import GroupsListPage from './pages/admin/GroupsListPage';
import GroupCreatePage from './pages/admin/GroupCreatePage';
import GroupEditPage from './pages/admin/GroupEditPage';
import ActivitiesListPage from './pages/admin/ActivitiesListPage';
import ActivityCreatePage from './pages/admin/ActivityCreatePage';
import ActivityEditPage from './pages/admin/ActivityEditPage';
import CoursesListPage from './pages/admin/CoursesListPage';
import SectionsListPage from './pages/admin/SectionsListPage';
import CourseCreatePage from './pages/admin/CourseCreatePage';
import CourseEditPage from './pages/admin/CourseEditPage';
import MembersListPage from './pages/admin/MembersListPage';
import MemberCreatePage from './pages/admin/MemberCreatePage';
import MemberEditPage from './pages/admin/MemberEditPage';
import UsersListPage from './pages/admin/UsersListPage';
import UserCreatePage from './pages/admin/UserCreatePage';
import UserEditPage from './pages/admin/UserEditPage';
import EventsListPage from './pages/admin/EventsListPage';
import EventCreatePage from './pages/admin/EventCreatePage';
import EventEditPage from './pages/admin/EventEditPage';
import EventTemplatesListPage from './pages/admin/EventTemplatesListPage';
import EventTemplateCreatePage from './pages/admin/EventTemplateCreatePage';
import EventTemplateEditPage from './pages/admin/EventTemplateEditPage';
import MembershipsListPage from './pages/admin/MembershipsListPage';
import MembershipCreatePage from './pages/admin/MembershipCreatePage';
import MembershipEditPage from './pages/admin/MembershipEditPage';
import ApprovalsPage from './pages/admin/ApprovalsPage';
import EventCalendarPage from './pages/admin/EventCalendarPage';
import AdminPreferencesPage from './pages/admin/AdminPreferencesPage';
import AdminStatisticsPage from './pages/admin/AdminStatisticsPage';
import AttendanceReportsPage from './pages/admin/AttendanceReportsPage';
import PaymentImportPage from './pages/admin/PaymentImportPage';
import PaymentOverviewPage from './pages/admin/PaymentOverviewPage';
import OrganizationSettingsPage from './pages/admin/OrganizationSettingsPage';
import RequirementTypesPage from './pages/admin/RequirementTypesPage';
import RolesListPage from './pages/admin/RolesListPage';
import RoleCreatePage from './pages/admin/RoleCreatePage';
import RoleDetailPage from './pages/admin/RoleDetailPage';
import BillingPage from './pages/admin/BillingPage';
import AdminEventInvitationsPage from './pages/admin/AdminEventInvitationsPage';
import MyMembersPage from './pages/user/MyMembersPage';
import MemberDetailPage from './pages/user/MemberDetailPage';
import MyMembershipsPage from './pages/user/MyMembershipsPage';
import MyActivitiesPage from './pages/user/MyActivitiesPage';
import MyCalendarPage from './pages/user/MyCalendarPage';
import EventInvitationsPage from './pages/user/EventInvitationsPage';
import AnnouncementsPage from './pages/user/AnnouncementsPage';
import AdminAnnouncementsPage from './pages/admin/AdminAnnouncementsPage';
import UserPreferencesPage from './pages/user/UserPreferencesPage';
import MyPaymentsPage from './pages/user/MyPaymentsPage';
import CommunicationPage from './pages/CommunicationPage';
import PublicEventPage from './pages/PublicEventPage';
import PublicEventsListPage from './pages/PublicEventsListPage';
import ActivitiesPage from './pages/ActivitiesPage';
import PublicLayout from './components/PublicLayout';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import CookieConsent from './components/CookieConsent';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import SuperAdminDashboardPage from './pages/superadmin/DashboardPage';
import SuperAdminOrganizationsPage from './pages/superadmin/OrganizationsPage';
import SuperAdminOrganizationDetailPage from './pages/superadmin/OrganizationDetailPage';
import SuperAdminOrganizationCreatePage from './pages/superadmin/OrganizationCreatePage';
import SuperAdminUsersPage from './pages/superadmin/UsersPage';
import SuperAdminUserDetailPage from './pages/superadmin/UserDetailPage';
import TenantRoute from './components/TenantRoute';
import HomePage from './pages/HomePage';
import PricingPage from './pages/PricingPage';
import DemoRequestPage from './pages/demo/DemoRequestPage';
import DemoVerifyPage from './pages/demo/DemoVerifyPage';
import DemoSetupPage from './pages/demo/DemoSetupPage';
import ContactSubmissionsPage from './pages/superadmin/ContactSubmissionsPage';
import ServerErrorsPage from './pages/superadmin/ServerErrorsPage';
import { api, getTenantSlug } from './api/client';



/** Languages the UI ships translations for. */
const SUPPORTED_LANGUAGES = ['en', 'cs'];

/**
 * Marks that the visitor picked a language themselves. i18next's detector
 * caches the *browser* language in localStorage on first load, so the presence
 * of that cache says nothing about intent — only this marker does. Without it,
 * an org default could never be applied after the first page view.
 */
const LANGUAGE_EXPLICIT_KEY = 'eventik-language-explicit';

/**
 * Applies the organization's configured default language for visitors who
 * haven't chosen one, and keeps <html lang> in sync with the active language.
 * The lang attribute drives hyphenation, screen-reader pronunciation and SEO —
 * a Czech page served as lang="en" gets all three wrong.
 */
const LanguageSync: React.FC = () => {
  const { i18n: i18nInstance } = useTranslation();
  const { defaultLanguage: orgDefaultLanguage, isLoading: brandingLoading } = useBranding();

  useEffect(() => {
    if (brandingLoading) return;
    if (!orgDefaultLanguage) return;
    if (localStorage.getItem(LANGUAGE_EXPLICIT_KEY)) return; // visitor already chose
    const target = orgDefaultLanguage.split('-')[0];
    if (!SUPPORTED_LANGUAGES.includes(target)) return;
    if ((i18nInstance.language || '').split('-')[0] === target) return;
    i18nInstance.changeLanguage(target);
  }, [orgDefaultLanguage, brandingLoading, i18nInstance]);

  useEffect(() => {
    const applyLang = (lng: string) => {
      document.documentElement.lang = (lng || 'en').split('-')[0];
    };
    applyLang(i18nInstance.language);
    i18nInstance.on('languageChanged', applyLang);
    return () => { i18nInstance.off('languageChanged', applyLang); };
  }, [i18nInstance]);

  return null;
};

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  const changeLanguage = (lng: string) => {
    localStorage.setItem(LANGUAGE_EXPLICIT_KEY, '1');
    i18n.changeLanguage(lng);
    // Sync language preference to backend for email localization
    if (user) {
      api.profile.update({ preferences: { language: lng } }).catch(() => {
        // Non-critical — silently ignore failures
      });
    }
  };

  return (
    <div className="language-switcher">
      <button
        className={`lang-btn ${i18n.language === 'en' ? 'active' : ''}`}
        onClick={() => changeLanguage('en')}
      >
        EN
      </button>
      <button
        className={`lang-btn ${i18n.language === 'cs' || i18n.language === 'cs-CZ' ? 'active' : ''}`}
        onClick={() => changeLanguage('cs')}
      >
        CS
      </button>
    </div>
  );
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [broadcastOpen, setBroadcastOpen] = React.useState(false);
  const { user, logout, isOwner, isAdmin, isSuperAdmin } = useAuth();
  const { counts } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantSlug = getTenantSlug();
  const hasTenantContext = !!tenantSlug;
  const { logoUrl, organizationName, isLoading: brandingLoading } = useBranding();

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const closeMenu = () => setMenuOpen(false);

  // Use branding logo if available on tenant subdomain, otherwise use default Eventik logo
  const defaultLogo = '/eventik-logo-name.png';
  const displayLogo = hasTenantContext && !brandingLoading && logoUrl && logoUrl !== defaultLogo
    ? logoUrl
    : defaultLogo;
  const logoAlt = organizationName || 'Eventik';

  return (
    <header className="header">
      <nav className="nav">
        <Link to="/" className="logo" onClick={closeMenu}>
          <img
            src={displayLogo}
            alt={logoAlt}
            className="logo-img"
            onError={(e) => { e.currentTarget.src = defaultLogo; }}
          />
        </Link>

        <div className="nav-right">
          <div className="nav-desktop-only">
            {user && <OrgSwitcher />}
          </div>
          <div className="nav-desktop-only">
            {user && hasTenantContext && (
              <button
                className="megaphone-btn"
                onClick={() => {
                  if (isAdmin || isOwner || isSuperAdmin) {
                    setBroadcastOpen(true);
                  } else {
                    navigate('/dashboard/announcements');
                  }
                }}
                aria-label={t('announcements.title')}
                title={t('announcements.title')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M16.881 4.346A23.112 23.112 0 018.25 6H7.5a5.25 5.25 0 00-.88 10.427 21.593 21.593 0 001.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.592.772-2.468a17.116 17.116 0 01-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0018 11.25c0-2.413-.393-4.735-1.119-6.904zM18.26 3.74a23.22 23.22 0 011.24 7.51 23.22 23.22 0 01-1.24 7.51c-.055.161.044.343.206.398a.3.3 0 00.095.017c.128 0 .247-.08.29-.207a23.79 23.79 0 001.149-4.395 23.79 23.79 0 00-1.149-4.395.3.3 0 00-.391-.205.299.299 0 00-.2.367z" />
                </svg>
                {counts.announcements > 0 && (
                  <span className="megaphone-badge">{counts.announcements}</span>
                )}
              </button>
            )}
          </div>
          <div className="nav-desktop-only">
            {user && hasTenantContext && <NotificationBell />}
          </div>
          <div className="nav-desktop-only">
            <LanguageSwitcher />
          </div>
          <button
            className={`nav-hamburger ${menuOpen ? 'open' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle Menu"
          >
            <span className="hamburger-bar"></span>
            <span className="hamburger-bar"></span>
            <span className="hamburger-bar"></span>
          </button>

          <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
            <li className="nav-mobile-only">
              <LanguageSwitcher />
            </li>
            {user && (
              <li className="nav-mobile-only">
                <OrgSwitcher />
              </li>
            )}
            {user && hasTenantContext && (
              <li className="nav-mobile-only nav-mobile-actions">
                <button
                  className="megaphone-btn"
                  onClick={() => {
                    closeMenu();
                    if (isAdmin || isOwner || isSuperAdmin) {
                      setBroadcastOpen(true);
                    } else {
                      navigate('/dashboard/announcements');
                    }
                  }}
                  aria-label={t('announcements.title')}
                  title={t('announcements.title')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M16.881 4.346A23.112 23.112 0 018.25 6H7.5a5.25 5.25 0 00-.88 10.427 21.593 21.593 0 001.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.592.772-2.468a17.116 17.116 0 01-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0018 11.25c0-2.413-.393-4.735-1.119-6.904zM18.26 3.74a23.22 23.22 0 011.24 7.51 23.22 23.22 0 01-1.24 7.51c-.055.161.044.343.206.398a.3.3 0 00.095.017c.128 0 .247-.08.29-.207a23.79 23.79 0 001.149-4.395 23.79 23.79 0 00-1.149-4.395.3.3 0 00-.391-.205.299.299 0 00-.2.367z" />
                  </svg>
                  {counts.announcements > 0 && (
                    <span className="megaphone-badge">{counts.announcements}</span>
                  )}
                </button>
                <NotificationBell />
              </li>
            )}
            {user ? (
              <>
                {/* View switcher - tenant subdomain only */}
                {hasTenantContext && (isAdmin || isOwner || isSuperAdmin) && (
                  <li className="nav-view-switcher-item">
                    <div className="view-switcher">
                      <Link
                        to="/dashboard/calendar"
                        className={`view-switcher-btn ${location.pathname.startsWith('/dashboard') ? 'active' : ''}`}
                        onClick={closeMenu}
                        title={t('nav.myDashboard')}
                      >
                        <i className="fas fa-user" />
                        <span>{t('nav.member')}</span>
                      </Link>
                      <Link
                        to="/admin/calendar"
                        className={`view-switcher-btn ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
                        onClick={closeMenu}
                        title={t('nav.admin')}
                      >
                        <i className="fas fa-cog" />
                        <span>{t('nav.adminShort')}</span>
                      </Link>
                    </div>
                  </li>
                )}
                {/* Dashboard link for non-admin users */}
                {hasTenantContext && !isAdmin && !isOwner && !isSuperAdmin && (
                  <li><Link to="/dashboard/calendar" onClick={closeMenu}>{t('nav.myDashboard')}</Link></li>
                )}
                {/* Super admin link - always navigate to main domain */}
                {isSuperAdmin && (
                  <li><a href={hasTenantContext ? superAdminUrl() : '/super-admin'} onClick={closeMenu}>{t('nav.superAdmin')}</a></li>
                )}
                <li className="nav-mobile-only user-menu-mobile">
                  <div className="user-menu-mobile-header">
                    <div className="user-menu-mobile-name">{user.name || user.email}</div>
                    {user.email && user.email !== user.name && (
                      <div className="user-menu-mobile-email">{user.email}</div>
                    )}
                  </div>
                </li>
                <li className="nav-mobile-only">
                  <Link
                    to={location.pathname.startsWith('/admin') ? '/admin/preferences' : '/dashboard/preferences'}
                    onClick={closeMenu}
                  >
                    {t('nav.preferences')}
                  </Link>
                </li>
                <li className="nav-mobile-only">
                  <Link to="#" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
                    {t('nav.logout')}
                  </Link>
                </li>
              </>
            ) : (
              <>
                {/* Only show login/register on tenant subdomains, not main landing page */}
                {hasTenantContext && (
                  <>
                    <li><Link to="/login" onClick={closeMenu}>{t('nav.login')}</Link></li>
                    <li><Link to="/register" onClick={closeMenu}>{t('nav.register')}</Link></li>
                  </>
                )}
              </>
            )}
          </ul>

          {user && (
            <div className="nav-desktop-only nav-user-menu-slot">
              <ImpersonationSwitcher />
              <UserMenu
                preferencesPath={location.pathname.startsWith('/admin') ? '/admin/preferences' : '/dashboard/preferences'}
                onLogout={handleLogout}
              />
            </div>
          )}
        </div>
      </nav>
      {broadcastOpen && (
        <BroadcastModal isOpen={broadcastOpen} onClose={() => setBroadcastOpen(false)} />
      )}
    </header>
  );
};

// Inline paths, no icon dependency — the house style everywhere else in this
// file. 24x24 viewBox, currentColor, so the footer's own colour drives them.
const SOCIAL_ICONS: Record<'instagram' | 'facebook' | 'website', React.ReactNode> = {
  instagram: (
    <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.8 3.8 0 01-1.38-.9c-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 5.68a4.16 4.16 0 100 8.32 4.16 4.16 0 000-8.32zm0 6.86a2.7 2.7 0 110-5.4 2.7 2.7 0 010 5.4zm5.3-7.02a.97.97 0 11-1.94 0 .97.97 0 011.94 0z" />
  ),
  facebook: (
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.52 1.49-3.91 3.77-3.91 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.78-1.63 1.57v1.89h2.78l-.45 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94z" />
  ),
  website: (
    <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm6.92 9h-3.03a15.6 15.6 0 00-1.2-5.5A8.02 8.02 0 0118.92 11zM12 4.04c.7.98 1.64 3.02 1.87 6.96h-3.74c.23-3.94 1.17-5.98 1.87-6.96zM4.26 15A7.96 7.96 0 014 13h3.06c.04 1.1.16 2.13.33 3.06H4.94c-.28-.34-.5-.7-.68-1.06zm.68-6.06h2.45A17.6 17.6 0 007.06 12H4c0-1.06.34-2.1.94-3.06zM4.94 17h2.62c.28 1.3.68 2.41 1.13 3.3A8.03 8.03 0 014.94 17zm4.19-2A15 15 0 018.8 13h6.4c-.05.72-.16 1.4-.33 2zm2.87 5.96c-.62-.87-1.4-2.5-1.76-4.96h3.52c-.36 2.46-1.14 4.09-1.76 4.96zm3.32-.66c.45-.89.85-2 1.13-3.3h2.62a8.03 8.03 0 01-3.75 3.3zM19.06 15h-2.45c.17-.93.29-1.96.33-3.06H20c0 1.06-.34 2.1-.94 3.06z" />
  ),
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { socialLinks } = useBranding();
  const networks = (['instagram', 'facebook', 'website'] as const)
    .filter((network) => !!socialLinks[network]);

  return (
    <footer className="footer">
      {networks.length > 0 && (
        <ul className="footer__socials">
          {networks.map((network) => {
            // Derived from the stored URL — no second field to keep in sync.
            // Unreadable URLs fall back to the icon on its own.
            const label = deriveSocialLabel(network, socialLinks[network]);
            const name = t(`footer.social.${network}`);
            return (
              <li key={network}>
                <a
                  className="footer__social"
                  href={socialLinks[network]}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label ? `${name}: ${label}` : name}
                  title={label ?? name}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    {SOCIAL_ICONS[network]}
                  </svg>
                  {label && <span className="footer__social-label">{label}</span>}
                </a>
              </li>
            );
          })}
        </ul>
      )}
      <p>&copy; {new Date().getFullYear()} {t('footer.copyright')}</p>
    </footer>
  );
};

const AppChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  return pathname === '/' && !getTenantSlug() ? null : <>{children}</>;
};

const AppFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const isLanding = pathname === '/' && !getTenantSlug();
  return <div className={`App${isLanding ? ' App--landing' : ''}`}>{children}</div>;
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <BrandingProvider>
        <LanguageSync />
        <ThemeProvider>
          {/* ToastProvider must sit OUTSIDE NotificationProvider: the
              notification context raises toasts for incoming notifications
              via useToast(), so the toast context has to exist first. */}
          <ToastProvider>
          <NotificationProvider>
            <AppFrame>
            <AppChrome><Header /></AppChrome>
          <Routes>
          {/* Public Routes - Available on both main domain and tenant subdomains */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/pricing" element={<PricingPage />} />
          </Route>

          {/* Demo Routes - Available on main domain only */}
          <Route path="/demo" element={<DemoRequestPage />} />
          <Route path="/demo/verify/:token" element={<DemoVerifyPage />} />
          <Route path="/demo/setup" element={<DemoSetupPage />} />

          {/* Tenant-Only Public Routes - Require tenant subdomain */}
          <Route element={<TenantRoute />}>
            <Route element={<PublicLayout />}>
              <Route path="/open-events" element={<PublicEventsListPage />} />
              <Route path="/activities" element={<ActivitiesPage />} />
            </Route>
          </Route>

          {/* Public Routes (without sidebar) */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route element={<TenantRoute />}>
            <Route path="/auth/impersonate" element={<ImpersonateCallback />} />
            <Route path="/auth/org-switch" element={<OrgSwitchCallback />} />
            <Route path="/e/:slug" element={<PublicEventPage />} />
          </Route>

          {/* Admin Routes - Require tenant context */}
          <Route element={<TenantRoute />}>
            <Route path="/admin" element={<AdminRoute />}>
              <Route element={<AdminLayout />}>
                <Route path="calendar" element={<EventCalendarPage />} />
                <Route path="groups" element={<GroupsListPage />} />
                <Route path="groups/create" element={<GroupCreatePage />} />
                <Route path="groups/:id/edit" element={<GroupEditPage />} />
                <Route path="activities" element={<ActivitiesListPage />} />
                <Route path="activities/create" element={<ActivityCreatePage />} />
                <Route path="activities/:id/edit" element={<ActivityEditPage />} />
                <Route path="courses" element={<CoursesListPage />} />
                <Route path="courses/create" element={<CourseCreatePage />} />
                <Route path="courses/:id/edit" element={<CourseEditPage />} />
                <Route path="sections" element={<SectionsListPage />} />
                <Route path="memberships" element={<MembershipsListPage />} />
                <Route path="memberships/create" element={<MembershipCreatePage />} />
                <Route path="memberships/:id/edit" element={<MembershipEditPage />} />
                <Route path="approvals" element={<ApprovalsPage />} />
                <Route path="users" element={<UsersListPage />} />
                <Route path="users/create" element={<UserCreatePage />} />
                <Route path="users/:id/edit" element={<UserEditPage />} />
                <Route path="members" element={<MembersListPage />} />
                <Route path="members/create" element={<MemberCreatePage />} />
                <Route path="members/:id/edit" element={<MemberEditPage />} />
                <Route path="events" element={<EventsListPage />} />
                <Route path="events/create" element={<EventCreatePage />} />
                <Route path="events/:id/edit" element={<EventEditPage />} />
                <Route path="event-templates" element={<EventTemplatesListPage />} />
                <Route path="event-templates/create" element={<EventTemplateCreatePage />} />
                <Route path="event-templates/:id/edit" element={<EventTemplateEditPage />} />
                <Route path="preferences" element={<AdminPreferencesPage />} />
                <Route path="statistics" element={<AdminStatisticsPage />} />
                <Route path="attendance" element={<AttendanceReportsPage />} />
                <Route path="payments/import" element={<PaymentImportPage />} />
                <Route path="payments/overview" element={<PaymentOverviewPage />} />
                <Route path="organization" element={<OrganizationSettingsPage />} />
                <Route path="roles" element={<RolesListPage />} />
                <Route path="roles/create" element={<RoleCreatePage />} />
                <Route path="roles/:id" element={<RoleDetailPage />} />
                <Route path="requirement-types" element={<RequirementTypesPage />} />
                <Route path="billing" element={<BillingPage />} />
                <Route path="invitations" element={<AdminEventInvitationsPage />} />
                <Route path="open-events" element={<PublicEventsListPage />} />
                <Route path="announcements" element={<AdminAnnouncementsPage />} />
                <Route path="communication" element={<CommunicationPage />} />
              </Route>
            </Route>
          </Route>

          {/* User Dashboard Routes - Require tenant context */}
          <Route element={<TenantRoute />}>
            <Route path="/dashboard" element={<UserRoute />}>
              <Route element={<UserLayout />}>
                <Route index element={<Navigate to="calendar" replace />} />
                <Route path="calendar" element={<MyCalendarPage />} />
                <Route path="invitations" element={<EventInvitationsPage />} />
                <Route path="members" element={<MyMembersPage />} />
                <Route path="members/:id" element={<MemberDetailPage />} />
                <Route path="memberships" element={<MyMembershipsPage />} />
                <Route path="preferences" element={<UserPreferencesPage />} />
                <Route path="open-events" element={<PublicEventsListPage />} />
                <Route path="activities" element={<MyActivitiesPage />} />
                <Route path="announcements" element={<AnnouncementsPage />} />
                <Route path="payments" element={<MyPaymentsPage />} />
                <Route path="communication" element={<CommunicationPage />} />
              </Route>
            </Route>
          </Route>

          {/* Legacy /notifications redirect */}
          <Route element={<TenantRoute />}>
            <Route path="/notifications" element={<Navigate to="/dashboard/calendar" replace />} />
          </Route>

          {/* Super Admin Routes */}
          <Route path="/super-admin" element={<SuperAdminRoute />}>
            <Route element={<SuperAdminLayout />}>
              <Route index element={<SuperAdminDashboardPage />} />
              <Route path="organizations" element={<SuperAdminOrganizationsPage />} />
              <Route path="organizations/create" element={<SuperAdminOrganizationCreatePage />} />
              <Route path="organizations/:id" element={<SuperAdminOrganizationDetailPage />} />
              <Route path="users" element={<SuperAdminUsersPage />} />
              <Route path="users/:id" element={<SuperAdminUserDetailPage />} />
              <Route path="contact" element={<ContactSubmissionsPage />} />
              <Route path="errors" element={<ServerErrorsPage />} />
            </Route>
          </Route>
        </Routes>
            <AppChrome><Footer /></AppChrome>
            <ChatBubble />
            <CookieConsent />
            </AppFrame>
          </NotificationProvider>
          </ToastProvider>
        </ThemeProvider>
        </BrandingProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
